import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import type CharterBundleModalUspItem from './CharterBundleModalUspItem'
import LineMaldivianIcon from 'LuxKit/Icons/line/LineMaldivianIcon'
import LineClockWithTickIcon from 'LuxKit/Icons/line/LineClockWithTickIcon'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'

export function CharterPartnerIcon() {
  return (
    <Group direction="horizontal" gap={8} verticalAlign="center" style={{ height: rem(40) }}>
      <LuxuryEscapesSmallLogo fill="currentColor" height={25} width={25} />
      {/** this was being used as separator: LE x Maldives and doesn't need translated */}
      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
      <BodyText variant="large" aria-hidden="true">x</BodyText>
      <LineMaldivianIcon style={{ width: rem(110), height: rem(20) }} />
    </Group>
  )
}

type CharterLearnMoreUsp = React.ComponentProps<typeof CharterBundleModalUspItem>
type CharterLearnMoreUspWithId = CharterLearnMoreUsp & {
  uspId: MaldivesUspId
}
type CharterLearnMoreMobileUspWithId = Partial<CharterLearnMoreUsp> & {
  uspId: MaldivesUspId
}

export const MALDIVES_USP_IDS = {
  saveTransitTime: 'saveTransitTime',
  privateCharter: 'privateCharter',
  maximiseHolidayTime: 'maximiseHolidayTime',
  flexibleBookings: 'flexibleBookings',
} as const

export type MaldivesUspId = (typeof MALDIVES_USP_IDS)[keyof typeof MALDIVES_USP_IDS]

export function useMaldivesUspGroup1(): Array<CharterLearnMoreUspWithId> {
  const intl = useIntl()
  return [
    {
      uspId: MALDIVES_USP_IDS.saveTransitTime,
      title: intl.formatMessage({
        id: 'h9K9BN4mtVGxpLLiLuRn',
        defaultMessage: 'Save up to 20 hours in transit*',
      }),
      description: intl.formatMessage({
        id: 'hHEE-4LVIEghMNamwfdG',
        defaultMessage: 'Fly direct from Melbourne to Malé in less than 12 hours, with no more international stopovers.',
      }),
      icon: <LinePlaneDepartureIcon />,
      tabletIcon: <LinePlaneDepartureIcon size="XL" style={{ display: 'block' }} />,
    },
    {
      uspId: MALDIVES_USP_IDS.privateCharter,
      title: intl.formatMessage({
        id: 'uL2dVTzE3KfKC7WHwaQU',
        defaultMessage: 'Luxury Escapes Exclusive Service',
      }),
      description: intl.formatMessage({
        id: 'ZYubDGuPgujYH6PUOTc_',
        defaultMessage: 'Weekly direct flights aboard an Airbus A330-200, reserved exclusively for Luxury Escapes customers.',
      }),
      icon: <CharterPartnerIcon />,
      tabletIcon: <CharterPartnerIcon />,
    },
  ]
}

export function useMaldivesUspGroup2(): Array<CharterLearnMoreUspWithId> {
  const intl = useIntl()
  return [
    {
      uspId: MALDIVES_USP_IDS.maximiseHolidayTime,
      title: intl.formatMessage({
        id: 'ao1AJjmMme4BwgbDlPJR',
        defaultMessage: 'Maximise your holiday time',
      }),
      description: intl.formatMessage({
        id: 'i5GmAVP92IPfujarEEtN',
        defaultMessage: 'Our overnight flights paired with early arrival and late departure give you extra time to enjoy the Maldives.',
      }),
      icon: <LineClockWithTickIcon />,
      tabletIcon: <LineClockWithTickIcon size="XL" style={{ display: 'block' }} />,
    },
    {
      uspId: MALDIVES_USP_IDS.flexibleBookings,
      title: intl.formatMessage({
        id: 'g7wGqbk1maJkfc4m62d6',
        defaultMessage: 'Flexible bookings',
      }),
      description: intl.formatMessage({
        id: 'puapei8CasicGd58ThVW',
        defaultMessage: 'Experience peace of mind with free cancellation within 7 days of purchase and flexible date-change options. Conditions apply.',
      }),
      icon: <LineCalendarCheckIcon />,
      tabletIcon: <LineCalendarCheckIcon size="XL" style={{ display: 'block' }} />,
    },
  ]
}

export function useMaldivesUspMobile(): Array<CharterLearnMoreMobileUspWithId> {
  const intl = useIntl()
  return [
    {
      uspId: MALDIVES_USP_IDS.saveTransitTime,
      title: intl.formatMessage({
        id: 'oJ4NLHNGcMjvy2zHRXLc',
        defaultMessage: 'Save up to 20 hours in transit*',
      }),
      description: <FormattedMessage
        id="ySOd5Gnik-CKtCnnMuIj"
        defaultMessage="<strong>Save up to 20 hours in transit*</strong> with nonstop flights from Melbourne to Malé in less than 12 hours."
        
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LinePlaneDepartureIcon />,
    },
    {
      uspId: MALDIVES_USP_IDS.privateCharter,
      title: intl.formatMessage({
        id: 'pzH6neQ6erK5ReEXveKx',
        defaultMessage: 'Luxury Escapes Exclusive Service',
      }),
      description: <FormattedMessage
        id="b21YNrHiUit2PkiWBXZe"
        defaultMessage="<strong>Luxury Escapes Exclusive Service</strong> aboard a Maldivian Airlines Airbus A330-200."
        
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LineStarIcon />,
    },
    {
      uspId: MALDIVES_USP_IDS.maximiseHolidayTime,
      title: intl.formatMessage({
        id: 'Ul5ztZUOlFBT9GWibxx2',
        defaultMessage: 'Maximise your holiday time',
      }),
      description: <FormattedMessage
        id="KsPUICGZNF8ZPDZpJnpm"
        defaultMessage="<strong>Maximise your holiday time</strong> with early arrival and late departure."
        
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LineClockWithTickIcon />,
    },
    {
      uspId: MALDIVES_USP_IDS.flexibleBookings,
      title: intl.formatMessage({
        id: 'otR1iIwEmt_4HgBwi5BL',
        defaultMessage: 'Flexible bookings',
      }),
      description: <FormattedMessage
        id="uCMLgmUip9jjr1UlfUnw"
        defaultMessage="<strong>Free cancellation</strong> within 7 days and flexible date-change options. Conditions apply."
        
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LineCalendarCheckIcon />,
    },
  ]
}
